import React from 'react';
import { string, func, bool } from 'prop-types';
import { intlShape, injectIntl, FormattedMessage } from '../../../util/reactIntl';
import classNames from 'classnames';
import { propTypes} from '../../../util/types';
import { formatMoney } from '../../../util/currency';
import { ensureListing } from '../../../util/data';
import { createSlug } from '../../../util/urlHelpers';
import config from '../../../config';
import { types as sdkTypes } from '../../../util/sdkLoader';
const { Money } = sdkTypes;
import { NamedLink } from '../../../components';

import css from './JobCard.module.css';

const priceData = (price, intl) => {
  if (price && price.currency === config.currency && price instanceof Money) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

export const JobCardComponent = props => {
  const {
    className,
    rootClassName,
    intl,
    listing,
    stateOptions,
    areasCertifiedOptions,
    periodOptions,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const { title = '', price, publicData } = currentListing.attributes;
  const {
    license,
    customId,
    maxPrice,
    areasCertified,
    state,
    gradeLevels,
    weeks,
    period,
    estimatedHours,
  } = publicData;

  const slug = `${createSlug(title)}-${customId}`;

  const { priceTitle } = priceData(price, intl);

  // const previewAreasCertified = !!areasCertified?.length && areasCertified.map(i => areasCertifiedOptions.find(a => a.key === i));
  const previewAreasCertified = config.custom.areasCertifiedOptions.filter(objFromA => (publicData?.areasCertified?.find(objFromB => (objFromA.key === objFromB))));

  const getAreaCertifiedByParent = previewAreasCertified?.map((area) => {
    const parent = area.parent ? area.parent : 'Other';
    return { [parent]: [area.label] }
  });

  const associatedAreaCertifiedByParent = getAreaCertifiedByParent?.reduce((acc, curr) => {
    const key = Object.keys(curr)[0]
    const found = acc.find(i => i[key])
    if (!found) { acc.push(curr) }
    else { found[key] = [ ...found[key], ...curr[key] ]}
    return acc;
  }, []);

  const renderAreaCertified = (
    <span className={css.subItem}>{
      associatedAreaCertifiedByParent?.map(item => {
        return Object.entries(item).map(([key, value], i) => {
          return (
            <span className={css.itemParent} key={i}>
              <span className={css.itemParentValue}>{key} </span>
              ({value.map(v => <span className={css.itemSpan}>{v}</span>)})
            </span>
          )
        })
      })}
    </span>
  );

  const formattedState = !!state?.length && state.map(s =>
    <span key={s} className={css.itemSpan}>{stateOptions.find(i => i.key === s).label}</span>
  );
  const formattedGradeLevels = !!gradeLevels?.length && gradeLevels.map(i => <span key={i} className={css.itemSpan}>{i}</span>);

  const renderWeeks = weeks?.map(item => <span>{item}</span>);

  const previewPeriod = !!period?.length && period.map(i => periodOptions.find(p => p.key === i));
  const renderPeriod = previewPeriod?.map(item => <span>{item.value}</span>);

  return (
    <NamedLink className={classes} name="ListingJobPage" params={{ slug }}>
      <div className={css.info}>
        <h2 className={css.cardTitle}>
          {title}
        </h2>
        <div className={css.infoHolder}>
          <span className={css.infoLabel}>Subject Areas: </span>
          <span className={css.infoData}>{renderAreaCertified}</span>
        </div>
        {license && !!state?.length &&
          <div className={css.infoHolder}>
            <span className={css.infoLabel}>Required License:</span>
            <span className={css.infoData}>{formattedState}</span>
          </div>
        }
        {gradeLevels && !!gradeLevels.length &&
          <div className={css.infoHolder}>
            <span className={css.infoLabel}>Grade Levels:</span>
            <span className={css.infoData}>{formattedGradeLevels}</span>
          </div>
        }
        <div className={css.separator} />
        <div className={css.infoHolder} title={priceTitle}>
          <span className={css.infoLabel}>up to &nbsp; </span>
          <span className={css.infoData}><span className={css.priceValue}>{`$${maxPrice/100}`} </span> per hour</span>
        </div>
        <div className={css.separator} />
        {renderWeeks &&
          <div className={css.infoHolder}>
            <span className={css.infoLabel}>Days a week: </span>
            <span className={css.infoData}>{renderWeeks}</span>
          </div>
        }
        {renderPeriod &&
          <div className={css.infoHolder}>
            <span className={css.infoLabel}>Period: </span>
            <span className={css.infoData}>{renderPeriod}</span>
          </div>
        }
        {estimatedHours &&
          <div className={css.infoHolder}>
            <span className={css.infoLabel}>Estimated number of teaching hours per week: <span
              className={css.infoData}>{estimatedHours}</span>
            </span>
          </div>
        }
      </div>
      <div className={css.buttonHolder}>
        <NamedLink name="ListingJobPage" params={{ slug }} className={css.button}>
          <FormattedMessage id="SectionJobs.cardButton"/>
        </NamedLink>
      </div>
    </NamedLink>
  );
};

JobCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
  stateOptions: config.custom.stateOptions,
  areasCertifiedOptions: config.custom.areasCertifiedOptions,
  periodOptions: config.custom.periodOptions,
};

JobCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(JobCardComponent);
